import { Button, Stack, TextField } from "@mui/material";
import Typography from "@mui/material/Typography";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { Container } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import config from "../../config";
import AssetTypesDropdown from "../shared/AssetTypesDropdown/AssetTypesDropdown";

interface Props {
  visible: boolean;
  headerMessage: string;
  placeholder: string;
  maxTextLength?: number;
  onConfirm: (assetName: string, assetType: string) => void;
  onClose: () => void;
  existingSubstationSwitchgearNames?: string[];
}

const isFlexiSurveyViewer = config.flexiSurveysEnabled;

export function SwitchgearCreateDialog(props: Props) {
  const { t } = useTranslation();

  const { visible, headerMessage, placeholder, maxTextLength, onConfirm, onClose, existingSubstationSwitchgearNames } =
    props;

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: {
      switchgearName: "",
      assetType: "",
    },
  });

  const [assetName, setAssetName] = useState<string | undefined>(undefined);
  const [assetType, setAssetType] = useState<string | undefined>(undefined);

  function handleHide() {
    onClose();
    clearErrors();
  }

  const handleConfirmButtonClick = handleSubmit(() => {
    if ((!isFlexiSurveyViewer && assetName) || (isFlexiSurveyViewer && assetName && assetType)) {
      onClose();
      onConfirm(assetName, assetType ?? "Switchgear");
    }
  });

  function handleAssetNameChange(event: any) {
    const value = event.target.value;
    clearErrors("switchgearName");
    setValue("switchgearName", value);
    setAssetName(value);
  }

  const header = (
    <Typography variant="h5" component="p">
      {headerMessage}
    </Typography>
  );

  const footer = (
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "center", gap: 2 }}>
      <Button
        type="button"
        variant="contained"
        onClick={handleConfirmButtonClick}
        color="primary"
        style={{ margin: 0 }}
      >
        {t("common.add")}
      </Button>
      <Button type="button" variant="contained" onClick={handleHide} style={{ margin: 0 }}>
        {t("common.close")}
      </Button>
    </Stack>
  );

  function handleAssetTypeChange(assetType: string): void {
    errors.assetType && clearErrors("assetType");
    setValue("assetType", assetType, { shouldValidate: true });
    setAssetType(assetType);
  }

  return isFlexiSurveyViewer ? (
    <Dialog
      header={header}
      visible={visible}
      onHide={handleHide}
      style={{ width: "25vw", minWidth: "300px" }}
      footer={footer}
    >
      <Container fluid>
        <Stack alignItems="center" spacing={2} sx={{ padding: 2 }}>
          <AssetTypesDropdown
            selectedAssetType={assetType}
            onAssetTypeChange={handleAssetTypeChange}
            minWidth={"280px"}
            error={errors?.assetType?.message}
            {...register("assetType", {
              required: t("asset.empty", { val: t("asset.assetType") }).toString(),
            })}
          />

          <TextField
            value={assetName}
            size="small"
            sx={{ width: "280px" }}
            error={!!errors?.switchgearName}
            helperText={
              isFlexiSurveyViewer && errors?.switchgearName && errors.switchgearName.type === "notEmpty"
                ? t("asset.emptyAssetName")
                : !isFlexiSurveyViewer && errors?.switchgearName && errors.switchgearName.type === "notEmpty"
                  ? t("switchgear.emptySwitchboardName")
                  : isFlexiSurveyViewer && errors?.switchgearName?.type === "notDuplicate"
                    ? t("asset.assetDuplication")
                    : !isFlexiSurveyViewer && errors?.switchgearName?.type === "notDuplicate"
                      ? t("switchgear.switchgearDuplication")
                      : ""
            }
            inputProps={{ maxLength: maxTextLength }}
            placeholder={placeholder}
            {...register("switchgearName", {
              value: assetName,
              validate: {
                notEmpty: (value) => value && value !== "",
                notDuplicate: (value) =>
                  !existingSubstationSwitchgearNames?.some((name) => name.toLowerCase() === value.toLowerCase()),
              },
            })}
            onChange={handleAssetNameChange}
            variant="outlined"
          />
        </Stack>
      </Container>
    </Dialog>
  ) : (
    <Dialog header={header} visible={visible} onHide={handleHide} style={{ width: "20vw" }} footer={footer}>
      <Stack alignItems="center" spacing={2} sx={{ padding: 2 }}>
        <TextField
          value={assetName}
          size="small"
          sx={{ width: "250px" }}
          error={!!errors?.switchgearName}
          helperText={
            errors?.switchgearName && errors.switchgearName.type === "notEmpty"
              ? t("switchgear.emptySwitchboardName")
              : errors?.switchgearName?.type === "notDuplicate"
                ? t("switchgear.switchgearDuplication")
                : ""
          }
          inputProps={{ maxLength: maxTextLength }}
          placeholder={placeholder}
          {...register("switchgearName", {
            value: assetName,
            validate: {
              notEmpty: (value) => value && value !== "",
              notDuplicate: (value) =>
                !existingSubstationSwitchgearNames?.some((name) => name.toLowerCase() === value.toLowerCase()),
            },
          })}
          onChange={handleAssetNameChange}
          variant="outlined"
        />
      </Stack>
    </Dialog>
  );
}
